import React from "react";
import Section from "../ui/Section/Section";
import { Grid } from "../ui/Grid/Grid";
import ChartCard from "../ui/Card/ChartCard";
import { CenterRow } from "../ui/Card/Card";
import Donut from "../charts/Donut";
import Sparkline from "../charts/Sparkline";
import Legend from "../ui/Legend/Legend";
import MiniMetric from "../ui/MiniMetric/MiniMetric";
import DeltaValue from "../ui/DeltaValue/DeltaValue";
import SectionSkeleton from "../ui/SectionSkeleton/SectionSkeleton";
import { SECTIONS } from "../../constants";
import { fmt1, fmt1Signed, fmtN } from "../../functions/format";
import { pctChange } from "../../functions/mapEffectif";
import { CHART_COLORS } from "../../styles/theme";
import { usePilotageRhStore } from "../../store/usePilotageRhStore";

const { id, title } = SECTIONS.absences;

const SKELETON_ROWS = [{ cols: 3, cards: [5, 4, 5] }];

/** Textes des tooltips "vs précédent" ; ajuster les suffixes ici. */
const formatPointDeltaTip = (current, previous) =>
    current !== null &&
    current !== undefined &&
    previous !== null &&
    previous !== undefined
        ? `${fmt1Signed(current - previous)} pt vs précédent`
        : undefined;

const formatPctDeltaTip = (current, previous) => {
    if (current === null || current === undefined) return undefined;
    const delta = pctChange(current, previous);
    return delta !== null ? `${fmt1Signed(delta)}% vs précédent` : undefined;
};

const AbsencesSection = ({ data, isLoading }) => {
    const compareOn = usePilotageRhStore((s) => !!s.compare[id]);

    if (isLoading && !data) {
        return (
            <Section id={id} title={title}>
                <SectionSkeleton rows={SKELETON_ROWS} />
            </Section>
        );
    }

    if (!data) return null;

    const { joursTravailles, joursAbsence, motifs, evolutionLabels, evolutionTaux } = data;
    const totalJours = joursTravailles + joursAbsence;
    const totalJoursPrevious =
        data.joursTravaillesPrevious != null && data.joursAbsencePrevious != null
            ? data.joursTravaillesPrevious + data.joursAbsencePrevious
            : null;

    return (
        <Section id={id} title={title}>
            <Grid cols={3}>
                <ChartCard title="Absences vs jours travaillés" fill>
                    <CenterRow>
                        <Donut
                            segments={
                                totalJours > 0
                                    ? [
                                          {
                                              value: joursTravailles,
                                              color: CHART_COLORS.violet,
                                              tip: `Jours travaillés : ${fmtN(
                                                  joursTravailles
                                              )} j`,
                                          },
                                          {
                                              value: joursAbsence,
                                              color: CHART_COLORS.blue,
                                              tip: `Jours d’absence : ${fmt1(
                                                  joursAbsence
                                              )} j`,
                                          },
                                      ]
                                    : [{ value: 1, color: CHART_COLORS.line }]
                            }
                            center={fmtN(totalJours)}
                            sub="JOURS"
                            centerTip={
                                compareOn
                                    ? formatPctDeltaTip(
                                          totalJours,
                                          totalJoursPrevious
                                      )
                                    : undefined
                            }
                        />
                    </CenterRow>
                    <Legend
                        items={[
                            {
                                name: `Jours travaillés ${fmtN(
                                    joursTravailles
                                )} j`,
                                color: CHART_COLORS.violet,
                            },
                            {
                                name: `Jours d’absence ${fmt1(joursAbsence)} j`,
                                color: CHART_COLORS.blue,
                            },
                        ]}
                    />
                </ChartCard>

                <ChartCard title="Absentéisme" fill>
                    {evolutionTaux.length > 0 && (
                        <Sparkline
                            points={evolutionTaux}
                            tips={evolutionLabels.map(
                                (l, i) => `${l} : ${fmt1(evolutionTaux[i])}%`
                            )}
                        />
                    )}
                    <MiniMetric
                        mt="8px"
                        label="Taux actuel"
                        value={
                            <DeltaValue
                                section={id}
                                tip={formatPointDeltaTip(
                                    data.taux,
                                    data.tauxPrevious
                                )}
                            >
                                {`${fmt1(data.taux)}%`}
                            </DeltaValue>
                        }
                    />
                    <MiniMetric
                        label="Durée moyenne d’absence"
                        value={
                            <DeltaValue
                                section={id}
                                tip={formatPctDeltaTip(
                                    data.dureeMoyenne,
                                    data.dureeMoyennePrevious
                                )}
                            >
                                {`${fmt1(data.dureeMoyenne)} jours`}
                            </DeltaValue>
                        }
                    />
                    <MiniMetric
                        label="Fréquence d’absence"
                        value={
                            <DeltaValue
                                section={id}
                                tip={formatPctDeltaTip(
                                    data.frequence,
                                    data.frequencePrevious
                                )}
                            >
                                {`${fmt1(data.frequence)} arr./collab.`}
                            </DeltaValue>
                        }
                    />
                </ChartCard>

                <ChartCard title="Absences par motif" fill>
                    <CenterRow>
                        <Donut
                            segments={
                                motifs.length
                                    ? motifs.map((m) => ({
                                          value: m.value,
                                          color: m.color,
                                          tip: `${m.name} : ${fmt1(m.value)} j`,
                                      }))
                                    : [{ value: 1, color: CHART_COLORS.line }]
                            }
                            center={fmt1(joursAbsence)}
                            sub="JOURS"
                            centerTip={
                                compareOn
                                    ? formatPctDeltaTip(
                                          joursAbsence,
                                          data.joursAbsencePrevious
                                      )
                                    : undefined
                            }
                        />
                    </CenterRow>
                    <Legend
                        items={motifs.map((m) => ({
                            name: `${m.name} ${m.pct}%`,
                            color: m.color,
                        }))}
                    />
                </ChartCard>
            </Grid>
        </Section>
    );
};

export default AbsencesSection;
